import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import Navbar from "./Navbar";
import CanchaForm from "../canchas/CanchaForm";
import CanchaList from "../canchas/CanchaList";
import EstablecimientoPerfilForm from "../establecimiento/EstablecimientoPerfilForm";
import ReservasCalendar from "./ReservasCalendar";

import { userService } from "../../services/userService";
import { reservaService } from "../../services/reservaService";
import { canchaService } from "../../services/canchaService";

/**
 * Utilidades pequeñas
 */
const formatDate = (value) => {
  if (!value) return "";
  try {
    // Acepta "YYYY-MM-DD" o ISO y muestra "DD/MM/YYYY"
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      // fallback manual si viniera como "YYYY-MM-DD"
      const parts = String(value).split("T")[0].split("-");
      if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
      return String(value);
    }
    return new Intl.DateTimeFormat("es-PE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(d);
  } catch {
    return String(value);
  }
};

const formatCurrencyPEN = (n) => {
  if (n === null || n === undefined || isNaN(Number(n))) {
    return "";
  }
  try {
    return new Intl.NumberFormat("es-PE", {
      style: "currency",
      currency: "PEN",
      minimumFractionDigits: 2,
    }).format(Math.abs(Number(n)));
  } catch {
    return `S/ ${Math.abs(Number(n)).toFixed(2)}`;
  }
};

/**
 * Formulario: crear reserva presencial desde el establecimiento
 */
function CrearReservaEstablecimientoForm({
  onClose,
  onCreated,
  establecimientoId,
}) {
  const [canchas, setCanchas] = useState([]);
  const clienteGenericoId = null; // si no hay cliente explícito
  const [form, setForm] = useState({
    cliente_id: establecimientoId || clienteGenericoId,
    cancha_id: "",
    fecha: "",
    hora_inicio: "",
    hora_fin: "",
    metodo_pago: "efectivo",
    observaciones: "",
  });
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  // Handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === "hora_inicio") {
      // si cambia la hora de inicio y hora_fin quedó antes, la limpiamos
      setForm((prev) => ({ ...prev, hora_fin: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg("");
    setLoading(true);
    try {
      // Ajusta este método al que tengas en tu service
      // Por ejemplo: reservaService.storeEstablecimiento(form)
      const res = await reservaService.storeEstablecimiento({ ...form });
      setMsg("Reserva creada correctamente");
      if (typeof onCreated === "function") onCreated(res);
      // reset básico
      setForm((prev) => ({
        ...prev,
        cancha_id: "",
        fecha: "",
        hora_inicio: "",
        hora_fin: "",
        metodo_pago: "efectivo",
        observaciones: "",
      }));
    } catch (err) {
      console.error(err);
      const apiMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Error al crear la reserva";
      setMsg(apiMsg);
    } finally {
      setLoading(false);
    }
  };

  // Cargar canchas
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const canchasRes = await canchaService.getMisCanchas();
        if (mounted) setCanchas(canchasRes?.data ?? canchasRes ?? []);
      } catch (e) {
        setMsg("Error cargando canchas");
        console.error(e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Sincroniza cliente_id con establecimientoId si cambia
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      cliente_id: establecimientoId || clienteGenericoId,
    }));
  }, [establecimientoId]);

  // Generar opciones de hora en bloques de 30 minutos
  const generarOpcionesHora = (hora_inicio, hora_fin) => {
    const opciones = [];
    if (!hora_inicio || !hora_fin) return opciones;
    let [hIni, mIni] = hora_inicio.split(":").map(Number);
    let [hFin, mFin] = hora_fin.split(":").map(Number);
    let tIni = hIni * 60 + mIni;
    let tFin = hFin * 60 + mFin;
    for (let t = tIni; t <= tFin; t += 30) {
      let h = Math.floor(t / 60);
      let m = t % 60;
      let label = `${h.toString().padStart(2, "0")}:${m
        .toString()
        .padStart(2, "0")}`;
      opciones.push(label);
    }
    return opciones;
  };

  // Obtener horario de apertura/cierre de la cancha seleccionada
  const canchaSeleccionada = canchas.find(
    (c) => String(c.id) === String(form.cancha_id)
  );
  const horaApertura = canchaSeleccionada?.hora_apertura || "08:00";
  const horaCierre = canchaSeleccionada?.hora_cierre || "22:00";
  const opcionesInicio = generarOpcionesHora(horaApertura, horaCierre);

  // Opciones de fin: solo posteriores a la hora de inicio seleccionada
  let opcionesFin = [];
  if (form.hora_inicio) {
    const [hIni, mIni] = form.hora_inicio.split(":").map(Number);
    const tIni = hIni * 60 + mIni;
    opcionesFin = generarOpcionesHora(horaApertura, horaCierre).filter(
      (hora) => {
        const [h, m] = hora.split(":").map(Number);
        const t = h * 60 + m;
        return t > tIni;
      }
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6 bg-white rounded-2xl shadow-2xl p-8 border border-green-200 max-w-md mx-auto animate-fadeIn"
    >
      <h2 className="text-3xl font-extrabold text-center text-green-800 mb-6 tracking-tight flex items-center justify-center gap-2">
        <svg
          className="w-8 h-8 text-green-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
          />
        </svg>
        Nueva Reserva
      </h2>

      <div className="mb-4">
        <label className="block text-base font-semibold text-gray-700 mb-1">
          Cancha
        </label>
        <select
          name="cancha_id"
          value={form.cancha_id}
          onChange={handleChange}
          required
          className="mt-1 block w-full border border-green-400 rounded-lg shadow focus:ring-green-500 focus:border-green-500 px-3 py-2 text-lg"
        >
          <option value="">Selecciona una cancha</option>
          {canchas.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nombre}
            </option>
          ))}
        </select>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="flex-1">
          <label className="block text-base font-semibold text-gray-700 mb-1">
            Fecha
          </label>
          <input
            type="date"
            name="fecha"
            value={form.fecha}
            onChange={handleChange}
            required
            min={
              new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
                .toISOString()
                .split("T")[0]
            }
            className="mt-1 block w-full border border-green-400 rounded-lg shadow focus:ring-green-500 focus:border-green-500 px-3 py-2 text-lg"
          />
        </div>
        <div className="flex-1">
          <label className="block text-base font-semibold text-gray-700 mb-1">
            Hora inicio
          </label>
          <select
            name="hora_inicio"
            value={form.hora_inicio}
            onChange={handleChange}
            required
            className="mt-1 block w-full border border-green-400 rounded-lg shadow focus:ring-green-500 focus:border-green-500 px-3 py-2 text-lg"
          >
            <option value="">Selecciona</option>
            {opcionesInicio.map((hora) => (
              <option key={hora} value={hora}>
                {hora}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <label className="block text-base font-semibold text-gray-700 mb-1">
            Hora fin
          </label>
          <select
            name="hora_fin"
            value={form.hora_fin}
            onChange={handleChange}
            required
            className="mt-1 block w-full border border-green-400 rounded-lg shadow focus:ring-green-500 focus:border-green-500 px-3 py-2 text-lg"
          >
            <option value="">Selecciona</option>
            {opcionesFin.map((hora) => (
              <option key={hora} value={hora}>
                {hora}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-base font-semibold text-gray-700 mb-1">
          Método de pago
        </label>
        <select
          name="metodo_pago"
          value={form.metodo_pago}
          onChange={handleChange}
          className="mt-1 block w-full border border-green-400 rounded-lg shadow focus:ring-green-500 focus:border-green-500 px-3 py-2 text-lg"
          required
        >
          <option value="efectivo">Efectivo</option>
          <option value="mercadopago">MercadoPago</option>
        </select>
      </div>

      <div className="mb-4">
        <label className="block text-base font-semibold text-gray-700 mb-1">
          Observaciones
        </label>
        <textarea
          name="observaciones"
          value={form.observaciones}
          onChange={handleChange}
          className="mt-1 block w-full border border-green-400 rounded-lg shadow focus:ring-green-500 focus:border-green-500 px-3 py-2 text-lg min-h-[60px]"
        />
      </div>

      {msg && (
        <div className="text-base text-center text-green-800 font-bold bg-green-50 border border-green-300 rounded p-3 mb-2">
          {msg}
        </div>
      )}

      <div className="flex justify-end gap-3 mt-4">
        <button
          type="button"
          className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-200 transition text-lg font-semibold"
          onClick={onClose}
          disabled={loading}
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-700 text-white font-bold rounded-lg shadow-lg hover:from-green-600 hover:to-green-800 transition text-lg"
          disabled={loading}
        >
          {loading ? "Guardando..." : "Crear Reserva"}
        </button>
      </div>
    </form>
  );
}

/**
 * Dashboard del establecimiento
 */
const EstablishmentDashboard = () => {
  const [showCanchaModal, setShowCanchaModal] = useState(false);
  const [showPerfilModal, setShowPerfilModal] = useState(false);
  const [showCrearReservaModal, setShowCrearReservaModal] = useState(false);

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState("dashboard"); // 'dashboard' | 'canchas' | 'reservas' | 'reportes'

  // Estado para reportes
  const [reportData, setReportData] = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);
  const [errorReport, setErrorReport] = useState(null);

  const [reservas, setReservas] = useState([]);
  const [loadingReservas, setLoadingReservas] = useState(true);
  const [errorReservas, setErrorReservas] = useState(null);

  const slotMinTime = useMemo(
    () => dashboardData?.establishment?.hora_apertura || "08:00:00",
    [dashboardData]
  );
  const slotMaxTime = useMemo(
    () => dashboardData?.establishment?.hora_cierre || "22:00:00",
    [dashboardData]
  );

  // Dashboard initial load
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await userService.establishmentDashboard();
        setDashboardData(response.data ?? response);
      } catch (err) {
        console.error(err);
        setError("Error al cargar datos del dashboard");
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  // Reporte ingresos
  const fetchReportIngresos = async () => {
    setLoadingReport(true);
    setErrorReport(null);
    try {
      const data = await reservaService.getReporteIngresos();
      setReportData(data.data || data);
    } catch (e) {
      setErrorReport("Error al cargar reportes");
    } finally {
      setLoadingReport(false);
    }
  };

  // Reservas establecimiento
  const fetchReservasEstablecimiento = async () => {
    setLoadingReservas(true);
    setErrorReservas(null);
    try {
      const response = await reservaService.getReservasEstablecimiento();
      setReservas(response?.data ?? response ?? []);
    } catch (err) {
      console.error(err);
      setErrorReservas("Error al cargar reservas del establecimiento");
    } finally {
      setLoadingReservas(false);
    }
  };

  useEffect(() => {
    if (activeTab === "reservas") {
      fetchReservasEstablecimiento();
    }
    if (activeTab === "reportes") {
      fetchReportIngresos();
    }
  }, [activeTab]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div
              className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-gray-300 border-t-gray-600"
              role="status"
              aria-label="Cargando"
            />
            <p className="mt-2">Cargando dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Nav entre vistas */}
      <nav className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <ul className="flex gap-4 md:gap-6 py-2">
            {[
              { key: "dashboard", label: "Dashboard" },
              { key: "reservas", label: "Reservas Recibidas" },
              { key: "canchas", label: "Mis Canchas" },
              { key: "reportes", label: "Reportes" },
            ].map((tab) => (
              <li key={tab.key}>
                <button
                  className={`font-medium px-3 py-2 rounded transition-colors duration-150 ${
                    activeTab === tab.key
                      ? "bg-indigo-600 text-white shadow"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                  onClick={() => setActiveTab(tab.key)}
                  aria-current={activeTab === tab.key ? "page" : undefined}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-6 px-4">
        {activeTab === "dashboard" && (
          <>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            <h1 className="text-2xl font-bold text-gray-900 mb-6">
              Dashboard de Establecimiento
            </h1>

            {dashboardData && (
              <>
                {/* Información del Establecimiento */}
                <div className="bg-white rounded-lg shadow mb-6">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">
                      Mi Establecimiento
                    </h3>
                    <button
                      className="ml-4 bg-indigo-600 text-white px-3 py-1 rounded text-sm hover:bg-indigo-700"
                      onClick={() => setShowPerfilModal(true)}
                    >
                      Editar Perfil
                    </button>
                  </div>

                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Nombre del Negocio
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {dashboardData?.establishment?.nombre_negocio ||
                            dashboardData?.establishment?.name ||
                            "-"}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Dirección
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {dashboardData?.establishment?.direccion || "-"}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Teléfono
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {dashboardData?.establishment?.telefono || "-"}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Email
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {dashboardData?.establishment?.email || "-"}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Rol
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {dashboardData?.establishment?.role || "-"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Modal Perfil */}
                  {showPerfilModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto relative">
                        <button
                          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-2xl"
                          onClick={() => setShowPerfilModal(false)}
                          aria-label="Cerrar"
                        >
                          &times;
                        </button>
                        <EstablecimientoPerfilForm
                          user={dashboardData.establishment}
                          onUpdated={async () => {
                            setShowPerfilModal(false);
                            try {
                              const response =
                                await userService.establishmentDashboard();
                              setDashboardData(response.data ?? response);
                            } catch (e) {
                              console.error(e);
                              setError("Error al actualizar el perfil");
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Acciones Rápidas */}
                <div className="bg-white rounded-lg shadow">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">
                      Acciones Rápidas
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <button
                        className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                        onClick={() => setShowCanchaModal(true)}
                      >
                        <svg
                          className="w-8 h-8 text-gray-400 mx-auto mb-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                        <span className="block text-sm font-medium text-gray-900">
                          Agregar Cancha
                        </span>
                        <span className="block text-sm text-gray-500">
                          Crear una nueva cancha
                        </span>
                        <span className="sr-only">
                          Abrir formulario de cancha
                        </span>
                      </button>

                      <button
                        className="p-4 border-2 border-dashed border-green-400 rounded-lg hover:border-green-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                        onClick={() => setShowCrearReservaModal(true)}
                      >
                        <svg
                          className="w-8 h-8 text-green-400 mx-auto mb-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                        <span className="block text-sm font-medium text-gray-900">
                          Crear Reserva
                        </span>
                        <span className="block text-sm text-gray-500">
                          Registrar reserva presencial
                        </span>
                        <span className="sr-only">
                          Abrir formulario de reserva
                        </span>
                      </button>

                      <button className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors">
                        <svg
                          className="w-8 h-8 text-gray-400 mx-auto mb-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                          />
                        </svg>
                        <span className="block text-sm font-medium text-gray-900">
                          Ver Reportes
                        </span>
                        <span className="block text-sm text-gray-500">
                          Analizar estadísticas
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Modales */}
                {showCanchaModal && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg relative">
                      <button
                        className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-2xl"
                        onClick={() => setShowCanchaModal(false)}
                        aria-label="Cerrar"
                      >
                        &times;
                      </button>
                      <CanchaForm
                        onCreated={async () => {
                          setShowCanchaModal(false);
                          try {
                            const response =
                              await userService.establishmentDashboard();
                            setDashboardData(response.data ?? response);
                          } catch (e) {
                            console.error(e);
                          }
                        }}
                      />
                    </div>
                  </div>
                )}

                {showCrearReservaModal && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg relative">
                      <button
                        className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-2xl"
                        onClick={() => setShowCrearReservaModal(false)}
                        aria-label="Cerrar"
                      >
                        &times;
                      </button>
                      <div className="text-lg font-bold mb-4">
                        Crear Reserva
                      </div>
                      <CrearReservaEstablecimientoForm
                        establecimientoId={dashboardData?.establishment?.id}
                        onClose={() => setShowCrearReservaModal(false)}
                        onCreated={() => {
                          setShowCrearReservaModal(false);
                          // Siempre refresca reservas después de crear una
                          fetchReservasEstablecimiento();
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Mensaje de inicio */}
                {Number(dashboardData?.stats?.total_canchas ?? 0) === 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-blue-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">
                          ¡Bienvenido a CanchasPro!
                        </h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <p>
                            Para comenzar, agrega tu primera cancha haciendo
                            clic en "Agregar Cancha" arriba. Una vez que tengas
                            canchas configuradas, podrás recibir reservas y
                            gestionar tu establecimiento.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {activeTab === "canchas" && <CanchaList />}

        {activeTab === "reportes" && (
          <div className="mt-6">
            <h2 className="text-xl font-bold mb-4">
              Reporte de Ingresos Mensuales
            </h2>
            {errorReport && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                {errorReport}
              </div>
            )}
            {loadingReport ? (
              <div className="flex items-center justify-center h-32">
                <div
                  className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-gray-300 border-t-gray-600"
                  role="status"
                  aria-label="Cargando"
                />
                <p className="ml-2">Cargando reportes...</p>
              </div>
            ) : Array.isArray(reportData) && reportData.length > 0 ? (
              <>
                {/* Gráfico de barras de ingresos mensuales */}
                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl shadow p-6 mb-8 border border-green-100">
                  <h3 className="text-xl font-bold mb-4 text-green-800 flex items-center gap-2">
                    <svg
                      className="w-6 h-6 text-green-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 10h1m2 0h13M5 6h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z"
                      />
                    </svg>
                    Gráfico de Ingresos Mensuales
                  </h3>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart
                      data={reportData.map((row) => ({
                        mes: row.mes,
                        ingresos: Number(
                          row.ingresos ?? row.total ?? row.suma ?? 0
                        ),
                      }))}
                      margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#e5e7eb"
                      />
                      <XAxis
                        dataKey="mes"
                        tick={{
                          fontSize: 13,
                          fill: "#2563eb",
                          fontWeight: 600,
                        }}
                      />
                      <YAxis
                        tickFormatter={formatCurrencyPEN}
                        tick={{
                          fontSize: 13,
                          fill: "#059669",
                          fontWeight: 600,
                        }}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "#f0fdf4",
                          border: "1px solid #bbf7d0",
                          borderRadius: 8,
                        }}
                        formatter={(v) => formatCurrencyPEN(v)}
                        labelFormatter={(v) => `Mes: ${v}`}
                      />
                      <Legend
                        wrapperStyle={{ fontWeight: 600, color: "#059669" }}
                      />
                      <Bar
                        dataKey="ingresos"
                        fill="#4ade80"
                        name="Ingresos (PEN)"
                        radius={[8, 8, 0, 0]}
                        barSize={38}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Tabla de ingresos */}
                <div className="overflow-x-auto">
                  <table className="min-w-full rounded-xl overflow-hidden shadow border border-green-100">
                    <thead className="bg-gradient-to-r from-green-100 to-blue-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold text-green-800 uppercase tracking-wider">
                          Mes
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-green-800 uppercase tracking-wider">
                          Ingresos
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.map((row, idx) => (
                        <tr
                          key={idx}
                          className={idx % 2 === 0 ? "bg-white" : "bg-green-50"}
                        >
                          <td className="px-4 py-2 whitespace-nowrap">
                            <span className="inline-block bg-green-100 text-green-800 text-xs font-semibold rounded-full px-3 py-1 shadow-sm border border-green-200">
                              {row.mes}
                            </span>
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <span className="font-bold text-lg text-green-700 drop-shadow-sm">
                              {formatCurrencyPEN(
                                row.ingresos ?? row.total ?? row.suma ?? 0
                              )}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {reportData.length > 0 && (
                        <tr className="bg-blue-50 border-t-2 border-green-200">
                          <td className="px-4 py-2 font-bold text-green-900 text-right">
                            Total
                          </td>
                          <td className="px-4 py-2 font-extrabold text-blue-900 text-lg">
                            {formatCurrencyPEN(
                              reportData.reduce(
                                (acc, row) =>
                                  acc +
                                  Number(
                                    row.ingresos ?? row.total ?? row.suma ?? 0
                                  ),
                                0
                              )
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="text-gray-500">No hay datos de ingresos.</div>
            )}
          </div>
        )}

        {activeTab === "reservas" && (
          <div className="mt-6">
            <h2 className="text-xl font-bold mb-4">Reservas Recibidas</h2>

            {errorReservas && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                {errorReservas}
              </div>
            )}

            {loadingReservas ? (
              <div className="flex items-center justify-center h-32">
                <div
                  className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-gray-300 border-t-gray-600"
                  role="status"
                  aria-label="Cargando"
                />
                <p className="ml-2">Cargando reservas...</p>
              </div>
            ) : Array.isArray(reservas) && reservas.length > 0 ? (
              <>
                <div className="mb-8 bg-white rounded-lg shadow p-4">
                  <ReservasCalendar
                    reservas={reservas}
                    slotMinTime={slotMinTime}
                    slotMaxTime={slotMaxTime}
                  />
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Fecha Registro
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Fecha Reserva
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Hora
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Cliente
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Cancha
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Estado
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reservas.map((reserva) => (
                        <tr key={reserva.id}>
                          <td className="px-4 py-2 whitespace-nowrap">
                            {formatDate(reserva.created_at)}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            {formatDate(reserva.fecha)}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            {reserva.hora_inicio} - {reserva.hora_fin}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            {reserva?.cliente?.name ? (
                              reserva.cliente.name
                            ) : reserva?.cliente_id ? (
                              reserva.cliente_id
                            ) : (
                              <span className="italic text-gray-400">
                                Cliente presencial
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            {reserva?.cancha?.nombre ?? reserva?.cancha_id}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap capitalize">
                            {reserva.estado}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            {formatCurrencyPEN(reserva.precio_total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="text-gray-500">No hay reservas registradas.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EstablishmentDashboard;
